import {
  Close as RadixClose,
  Content as RadixContent,
  Description as RadixDescription,
  Overlay as RadixOverlay,
  Portal as RadixPortal,
  Root as RadixRoot,
  Title as RadixTitle,
} from '@radix-ui/react-dialog';
import { useEffect, useRef, type ReactNode } from 'react';
import { useReducedMotion } from './media.js';
import { fadeMotion, motionStyle, surfaceMotion, type SurfacePresentation } from './motion.js';
import { usePrimitiveStyles, type DialogSize } from './styles.js';

/**
 * The shared modal shell behind both `<Dialog>` and `<Sheet>`.
 *
 * Radix does the parts that are easy to get wrong and expensive to get wrong:
 * focus trapping including shift-tab at the boundary, escape handling, scroll
 * locking, and hiding the rest of the page from a screen reader. Everything
 * below is the themed shell around that, plus the one decision Radix does not
 * make: whether this is a centered dialog or a bottom sheet.
 *
 * One thing this comment used to claim Radix handles and it does not, as wired:
 * restoring focus on close. See `restoreFocus` below.
 *
 * There is one implementation, not two, because a Sheet that drifts from a
 * Dialog is how a phone ends up with a subtly different confirm flow than a
 * laptop.
 */

/**
 * Optional props carry an explicit `| undefined`. The repo runs
 * `exactOptionalPropertyTypes`, and without it a caller could not pass a value
 * it computed conditionally (`description={maybeText}`), which is the normal
 * shape of a component call site. This matches how `@types/react` declares its
 * own props.
 */
export interface SurfaceProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Required. It names the dialog for a screen reader and it is always shown. */
  readonly title: string;
  readonly description?: string | undefined;
  readonly size?: DialogSize | undefined;
  /**
   * False for a question that has to be answered: escape and outside clicks
   * stop closing it, and the close affordance is not rendered. Use sparingly.
   */
  readonly dismissible?: boolean | undefined;
  readonly children?: ReactNode | undefined;
  readonly footer?: ReactNode | undefined;
  /**
   * Called once the surface has closed AND focus has been handed back to what
   * opened it. For the caller who wants to open something else next: anything
   * opened sooner has its focus taken off it by the return a moment later.
   */
  readonly onClosed?: (() => void) | undefined;
}

export interface DialogSurfaceProps extends SurfaceProps {
  readonly presentation: SurfacePresentation;
}

function preventDefault(event: { preventDefault: () => void }): void {
  event.preventDefault();
}

export function DialogSurface({
  open,
  onOpenChange,
  title,
  description,
  size = 'md',
  dismissible = true,
  presentation,
  children,
  footer,
  onClosed,
}: DialogSurfaceProps): ReactNode {
  usePrimitiveStyles();
  const reducedMotion = useReducedMotion();
  const motion = surfaceMotion(presentation, reducedMotion);

  // Spread rather than pass `undefined`: `exactOptionalPropertyTypes` makes an
  // explicit undefined a type error against Radix's optional handlers.
  const guards = dismissible
    ? {}
    : { onEscapeKeyDown: preventDefault, onInteractOutside: preventDefault };

  /**
   * Where focus goes when this closes.
   *
   * Radix restores focus to its `<Dialog.Trigger>`, and there is no trigger
   * here: dialogs open imperatively from the store, so nothing renders one.
   * Its default handler calls `preventDefault()` and then focuses a `triggerRef`
   * that is always null, and the `preventDefault()` has already cancelled
   * FocusScope's own fallback to the previously focused element. The result is
   * focus on `<body>` after every dismissal, which was verified in a live DOM
   * rather than reasoned about.
   *
   * Concretely: a keyboard user taps a ledger row forty rows down, confirms
   * "Void this play?", and lands back at the top of the document. WCAG 2.4.3,
   * and BUILD_PLAN 9.6 asks for full keyboard navigation in the scorebook
   * specifically.
   */
  const opener = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (open) opener.current = document.activeElement as HTMLElement | null;
  }, [open]);

  const restoreFocus = (event: Event) => {
    event.preventDefault();
    const target = opener.current;
    // `document.contains` because DialogHost swaps `key` when a queued dialog
    // follows this one, so the element that opened it may already be gone.
    if (target !== null && document.contains(target)) target.focus();
    // After the focus above, and not before: that is the whole point of it.
    onClosed?.();
  };

  return (
    <RadixRoot open={open} onOpenChange={onOpenChange} modal>
      <RadixPortal>
        <RadixOverlay className="gs-scrim" style={motionStyle(fadeMotion(reducedMotion))} />
        <div className="gs-viewport" data-presentation={presentation}>
          <RadixContent
            {...guards}
            onCloseAutoFocus={restoreFocus}
            className="gs-ui gs-surface"
            data-presentation={presentation}
            data-size={size}
            data-motion={motion.name}
            style={motionStyle(motion)}
          >
            {presentation === 'sheet' ? (
              <div className="gs-sheet-handle" aria-hidden="true" />
            ) : null}
            <RadixTitle className="gs-surface__title">{title}</RadixTitle>
            {description === undefined ? null : (
              <RadixDescription className="gs-surface__description">{description}</RadixDescription>
            )}
            {children === undefined ? null : <div className="gs-surface__body">{children}</div>}
            {footer === undefined ? null : <div className="gs-surface__footer">{footer}</div>}
            {dismissible ? (
              <RadixClose type="button" className="gs-surface__close" aria-label="Close">
                {'×'}
              </RadixClose>
            ) : null}
          </RadixContent>
        </div>
      </RadixPortal>
    </RadixRoot>
  );
}
